import { useQuery } from '@tanstack/react-query';

import { endpoints } from './api';

/** Public, session-independent branding, fetched once and cached for the run. */
export function useAppConfig() {
    return useQuery({
        queryKey: ['app-config'],
        queryFn: endpoints.appConfig,
        staleTime: Infinity,
        gcTime: Infinity,
    });
}

// defaultOwnStoreName mirrors the API's APP_OWN_STORE_NAME default, so the label
// reads correctly in the moment before the config request resolves rather than
// flashing empty.
const defaultOwnStoreName = 'Tezgah';

/**
 * The brand for the seller's own store, from the one place it is configured.
 *
 * Every surface that names the own store reads it through this hook — the
 * channel row already gets it from the API, and the nav, the shop page and the
 * copy that mentions it all resolve here — so renaming it is a single env change
 * with nothing hardcoded to hunt down.
 */
export function useOwnStoreName(): string {
    const config = useAppConfig();
    return config.data?.ownStoreName || defaultOwnStoreName;
}

/**
 * Where the terms of use and the privacy notice are published, from the API's
 * one setting for the marketing site (ADR 0018 §1). Undefined until the config
 * has been read: a link is drawn only to an address that came from it.
 */
export function useLegalLinks(): { termsUrl: string; privacyNoticeUrl: string } | undefined {
    const config = useAppConfig();
    if (!config.data?.termsUrl || !config.data.privacyNoticeUrl) return undefined;
    return { termsUrl: config.data.termsUrl, privacyNoticeUrl: config.data.privacyNoticeUrl };
}
